/**
 * GPU rain: instanced-style falling streaks as a single LineSegments draw
 * call. Each drop is two vertices sharing a base position; the vertex shader
 * animates the fall from a time uniform, so per-frame CPU cost is one
 * uniform write. Streaks brighten near the street lamps and neon so the
 * rain reads inside the light pools and fades in the dark between them.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  LineSegments,
  Scene,
  ShaderMaterial,
  Vector3
} from 'three'
import type { Vec3 } from '../core/types'
import { lerp } from './blend'
import {
  RAIN_ALPHA,
  RAIN_BASE_BRIGHTNESS,
  RAIN_COLOR,
  RAIN_DROP_COUNT,
  RAIN_FALL_SPEED,
  RAIN_FIELD_HEIGHT,
  RAIN_FIELD_X_MAX,
  RAIN_FIELD_X_MIN,
  RAIN_FIELD_Z_MAX,
  RAIN_FIELD_Z_MIN,
  RAIN_INDOOR_SCALE,
  RAIN_LIGHT_COUNT,
  RAIN_LIGHT_FALLOFF,
  RAIN_LIGHT_GAIN,
  RAIN_STREAK_LENGTH,
  RAIN_TAIL_FADE,
  RAIN_WIND_X
} from './constants'

export interface RainRig {
  /** Advance the fall; weight: 1 outdoor, 0 indoor. dim: finale multiplier. */
  update(dt: number, weight: number, dim: number): void
}

const VERTICES_PER_DROP = 2
/** Seamless loop period: one full traversal of the rain field. */
const CYCLE_SECONDS = RAIN_FIELD_HEIGHT / RAIN_FALL_SPEED

const VERTEX_SHADER = /* glsl */ `
  uniform float uTime;
  uniform float uIntensity;
  uniform vec3 uLights[${RAIN_LIGHT_COUNT}];
  attribute float aTip;
  varying float vBrightness;

  void main() {
    float fallY = mod(position.y - uTime * ${RAIN_FALL_SPEED.toFixed(2)},
      ${RAIN_FIELD_HEIGHT.toFixed(2)});
    vec3 streak = normalize(vec3(${RAIN_WIND_X.toFixed(3)}, -1.0, 0.0))
      * ${RAIN_STREAK_LENGTH.toFixed(3)};
    vec3 p = vec3(position.x, fallY, position.z) + aTip * streak;

    float brightness = ${RAIN_BASE_BRIGHTNESS.toFixed(4)};
    for (int i = 0; i < ${RAIN_LIGHT_COUNT}; i++) {
      float d = distance(p, uLights[i]);
      brightness += ${RAIN_LIGHT_GAIN.toFixed(3)}
        / (1.0 + d * d * ${RAIN_LIGHT_FALLOFF.toFixed(3)});
    }
    vBrightness = min(brightness, 1.0) * uIntensity * (1.0 - aTip * ${RAIN_TAIL_FADE.toFixed(2)});
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  varying float vBrightness;

  void main() {
    gl_FragColor = vec4(uColor * vBrightness, vBrightness * ${RAIN_ALPHA.toFixed(2)});
  }
`

const buildGeometry = (): BufferGeometry => {
  const vertexCount = RAIN_DROP_COUNT * VERTICES_PER_DROP
  const positions = new Float32Array(vertexCount * 3)
  const tips = new Float32Array(vertexCount)

  for (let drop = 0; drop < RAIN_DROP_COUNT; drop += 1) {
    const x = RAIN_FIELD_X_MIN + Math.random() * (RAIN_FIELD_X_MAX - RAIN_FIELD_X_MIN)
    const z = RAIN_FIELD_Z_MIN + Math.random() * (RAIN_FIELD_Z_MAX - RAIN_FIELD_Z_MIN)
    const phaseY = Math.random() * RAIN_FIELD_HEIGHT
    for (let vertex = 0; vertex < VERTICES_PER_DROP; vertex += 1) {
      const i = drop * VERTICES_PER_DROP + vertex
      positions[i * 3] = x
      positions[i * 3 + 1] = phaseY
      positions[i * 3 + 2] = z
      tips[i] = vertex
    }
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.setAttribute('aTip', new BufferAttribute(tips, 1))
  return geometry
}

const buildLightUniform = (lightPositions: readonly Vec3[]): Vector3[] => {
  const padded: Vector3[] = []
  for (let i = 0; i < RAIN_LIGHT_COUNT; i += 1) {
    const source = lightPositions[i] ?? lightPositions[lightPositions.length - 1]
    padded.push(source ? new Vector3(source.x, source.y, source.z) : new Vector3())
  }
  return padded
}

export const createRainRig = (scene: Scene, lightPositions: readonly Vec3[]): RainRig => {
  const timeUniform = { value: 0 }
  const intensityUniform = { value: 1 }
  const material = new ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    uniforms: {
      uTime: timeUniform,
      uIntensity: intensityUniform,
      uColor: { value: new Color(RAIN_COLOR) },
      uLights: { value: buildLightUniform(lightPositions) }
    },
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false
  })

  const lines = new LineSegments(buildGeometry(), material)
  lines.frustumCulled = false
  scene.add(lines)

  let time = 0

  const update = (dt: number, weight: number, dim: number): void => {
    time = (time + dt) % CYCLE_SECONDS
    timeUniform.value = time
    intensityUniform.value = lerp(RAIN_INDOOR_SCALE, 1, weight) * dim
  }

  return { update }
}
